import { closingBracket } from './brackets.js';
import { DAY_LENGTH_MS, addMonths, addYears, isGregorian, maxDaysInMonth, monthsInYear, msToParts, partsToMs, weekLength } from './calendar.js';
import { DEFAULT_TIME_DISPLAY, UNIT_MINUTES, formatCount, parseCount, parseStamp, stampText } from './timeline.js';
import type { SceneTime, TimePrecision, TimeUnit } from './types.js';

// ---------------------------------------------------------------------------
// Zeitmarken (PLAN.md §16): Eine Textstelle sagt, wann etwas spielt („am Morgen des 3. März 1823“, „drei Tage später“). Gespeichert
// wie ein Bezug als Endung hinter der Stelle: `[am Morgen des 3. März 1823]{zeit 1823-03-03T07:00 stunde ca}`. Die Formulierung bleibt
// die des Autors; die Marke kennt ihre Bedeutung. Werte:
//   1823 · 1823-03 · 1823-03-03 · 1823-03-03T07:00       absolutes Datum (bis Jahr, Monat, Tag bzw. Minute)
//   --03-03 · --03-03T07:00                             Tag im Jahr, das Jahr aus dem Zusammenhang („am 3. März“)
//   tag12 · tag12@06:00 · jahr340 · stunde5 · minute45   eigene Zählung
//   +3d · -2w · +1d@07:00 · +0d@22:00                   relativ zur vorigen Marke bzw. zur Ankerszene (min, h, d, w, mo, y)
//   3d · 2h · 45min                                     eine Dauer („drei Tage lang“): das Ende ist Beginn + Dauer
// Danach optional die Rolle (`beginn`, `ende`, `dauer`; ohne: die erste Marke ist der Beginn), die Genauigkeit (jahr, monat, tag,
// stunde, minute) und `ca` für ungefähr (PLAN.md §17).
// ---------------------------------------------------------------------------

const PRECISION_WORDS: Record<TimePrecision, string> = { year: 'jahr', month: 'monat', day: 'tag', hour: 'stunde', minute: 'minute' };
const WORD_PRECISION = Object.fromEntries(Object.entries(PRECISION_WORDS).map(([p, w]) => [w, p as TimePrecision])) as Record<string, TimePrecision>;

const ABS = /^(\d{4})(?:-(\d{2})(?:-(\d{2})(?:T(\d{2}):(\d{2}))?)?)?$/;
const YEARLESS = /^--(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?$/;
const COUNT = /^(minute|stunde|tag|jahr)(-?\d+)(?:@(\d{2}):(\d{2}))?$/;
const REL = /^([+-])(\d{1,6})(min|h|d|w|mo|y)(?:@(\d{2}):(\d{2}))?$/;
const DURATION = /^(\d{1,6})(min|h|d|w|mo|y)$/;
const SUFFIX = /^\{zeit[ \t]+([^\s{}]+)((?:[ \t]+(?:jahr|monat|tag|stunde|minute|ca|beginn|ende|dauer))*)[ \t]*\}/;

/** Rolle einer Zeitmarke: Beginn, Ende oder Dauer der Szene; null = von selbst (die erste Marke ist der Beginn) */
export type MarkRole = 'start' | 'end' | 'duration';
const ROLE_WORDS: Record<MarkRole, string> = { start: 'beginn', end: 'ende', duration: 'dauer' };
const WORD_ROLE = Object.fromEntries(Object.entries(ROLE_WORDS).map(([r, w]) => [w, r as MarkRole])) as Record<string, MarkRole>;

/** Eine Dauer (`3d`, `2h`, `45min`, `1w`, `2mo`, `1y`)? */
export const isDuration = (v: string): boolean => DURATION.test(v);

const clockOk = (h: string | undefined, m: string | undefined): boolean => h === undefined || (Number(h) <= 23 && Number(m) <= 59);
/** Monat und Tag im Kalender der Geschichte (gregorianisch wie bisher bis zum 31.) */
const monthOk = (mo: string | undefined): boolean => mo === undefined || (Number(mo) >= 1 && Number(mo) <= monthsInYear());
const dayOk = (mo: string | undefined, d: string | undefined): boolean => d === undefined || (Number(d) >= 1 && Number(d) <= (isGregorian() ? 31 : maxDaysInMonth(Number(mo))));

/** Ist das ein gültiger Wert einer Zeitmarke? */
export function isTimeMarkValue(v: string): boolean {
  let m = ABS.exec(v);
  if (m) return monthOk(m[2]) && dayOk(m[2], m[3]) && clockOk(m[4], m[5]);
  if ((m = YEARLESS.exec(v))) return monthOk(m[1]) && dayOk(m[1], m[2]) && clockOk(m[3], m[4]);
  if ((m = COUNT.exec(v))) return clockOk(m[3], m[4]);
  if ((m = REL.exec(v))) return clockOk(m[4], m[5]);
  return DURATION.test(v);
}

/** Relativ (zur vorigen Marke bzw. zur Ankerszene)? */
export const isRelativeMark = (v: string): boolean => REL.test(v) || YEARLESS.test(v);

/** Art eines Werts: Datum, Tag im Jahr, eigene Zählung, relativ oder Dauer (null: kein gültiger Wert) */
export type MarkFamily = 'abs' | 'yearless' | 'count' | 'rel' | 'duration';
export function markFamily(v: string): MarkFamily | null {
  if (!isTimeMarkValue(v)) return null;
  return ABS.test(v) ? 'abs' : YEARLESS.test(v) ? 'yearless' : COUNT.test(v) ? 'count' : REL.test(v) ? 'rel' : 'duration';
}

/** Genauigkeit, die der Wert selbst nahelegt: „1823“ → Jahr, „+3d“ → Tag, mit Uhrzeit → Minute. */
export function impliedPrecision(v: string): TimePrecision {
  let m = ABS.exec(v);
  if (m) return m[4] !== undefined ? 'minute' : m[3] !== undefined ? 'day' : m[2] !== undefined ? 'month' : 'year';
  if ((m = YEARLESS.exec(v))) return m[3] !== undefined ? 'minute' : 'day';
  if ((m = COUNT.exec(v))) return m[3] !== undefined || m[1] === 'minute' ? 'minute' : m[1] === 'stunde' ? 'hour' : m[1] === 'tag' ? 'day' : 'year';
  if ((m = REL.exec(v))) {
    if (m[4] !== undefined) return 'minute';
    return m[3] === 'min' ? 'minute' : m[3] === 'h' ? 'hour' : m[3] === 'd' || m[3] === 'w' ? 'day' : m[3] === 'mo' ? 'month' : 'year';
  }
  return 'minute';
}

export interface TimeMarkRef {
  /** `[` … bis hinter `}` */
  from: number;
  to: number;
  /** die Textstelle selbst (zwischen den Klammern) */
  textFrom: number;
  textTo: number;
  value: string;
  /** ausdrücklich angegebene Genauigkeit (sonst `impliedPrecision`) */
  precision: TimePrecision | null;
  approx: boolean;
  /** Beginn, Ende oder Dauer; null = von selbst (eine Dauer ist immer eine Dauer) */
  role: MarkRole | null;
}

/** Endung einer Zeitmarke im Markdown: `{zeit +1d@07:00 ende stunde ca}` (eine Dauer braucht kein `dauer`) */
export const timeMarkSuffix = (value: string, precision: TimePrecision | null, approx: boolean, role: MarkRole | null = null): string =>
  `{zeit ${value}${role && !(role === 'duration' && DURATION.test(value)) ? ` ${ROLE_WORDS[role]}` : ''}${precision ? ` ${PRECISION_WORDS[precision]}` : ''}${approx ? ' ca' : ''}}`;

/** Rolle einer Marke: die angegebene, eine Dauer ist immer eine Dauer */
export const markRole = (mark: Pick<TimeMarkRef, 'value' | 'role'>): MarkRole | null => (DURATION.test(mark.value) ? 'duration' : (mark.role ?? null));

/** Die Zeitmarke, die genau bei `at` (mit „[“) beginnt, sonst null. */
export function timeMarkAt(text: string, at: number): TimeMarkRef | null {
  if (text[at] !== '[' || (at > 0 && text[at - 1] === '\\')) return null;
  const close = closingBracket(text, at);
  if (close < 0) return null;
  const m = SUFFIX.exec(text.slice(close + 1, close + 1 + 200));
  if (!m || !isTimeMarkValue(m[1]!)) return null;
  const words = (m[2] ?? '').trim().split(/[ \t]+/).filter(Boolean);
  const precision = words.map((w) => WORD_PRECISION[w]).find((p): p is TimePrecision => !!p) ?? null;
  const role = words.map((w) => WORD_ROLE[w]).find((r): r is MarkRole => !!r) ?? null;
  return { from: at, to: close + 1 + m[0].length, textFrom: at + 1, textTo: close, value: m[1]!, precision, approx: words.includes('ca'), role };
}

/** Alle Zeitmarken eines Textes (Markdown), in Textreihenfolge (auch in Bezügen). */
export function timeMarksIn(text: string): TimeMarkRef[] {
  const out: TimeMarkRef[] = [];
  if (!text.includes(']{zeit')) return out;
  for (let i = text.indexOf('['); i >= 0; i = text.indexOf('[', i + 1)) {
    const t = timeMarkAt(text, i);
    if (t) out.push(t);
  }
  return out;
}

/** Text ohne Zeitmarken: die Stelle bleibt, Klammern und Zeitangabe fallen weg. */
export function stripTimeMarks(text: string): string {
  const marks = timeMarksIn(text);
  if (marks.length === 0) return text;
  let out = '';
  let last = 0;
  for (const t of marks) {
    if (t.from < last) continue;
    out += text.slice(last, t.from) + text.slice(t.textFrom, t.textTo);
    last = t.to;
  }
  return out + text.slice(last);
}

// ---------------------------------------------------------------------------
// Rechnen mit Zeiten
// ---------------------------------------------------------------------------

/** Ein Zeitpunkt zum Rechnen: Datum (Millisekunden) oder eigene Zählung (Minuten, mit der Einheit zur Anzeige). */
export interface Moment {
  basis: 'ms' | 'count';
  value: number;
  unit: TimeUnit | null;
}

/** Zeittext (wie gespeichert) → Zeitpunkt; null, wenn er keiner ist. */
export function momentOf(text: string | null | undefined): Moment | null {
  if (!text) return null;
  const ms = parseStamp(text);
  if (ms !== null) return { basis: 'ms', value: ms, unit: null };
  const c = parseCount(text);
  return c ? { basis: 'count', value: c.value * UNIT_MINUTES[c.unit], unit: c.unit } : null;
}

/** Datum im Kalender der Geschichte → Millisekunden; null, wenn es im Kalender keins ist */
const utc = (y: number, mo: number, d: number, h = 0, mi = 0): number | null => partsToMs({ y, mo, d, h, mi });

/** Zeitpunkt → Zeittext, wie er gespeichert wird (Datum `JJJJ-MM-TTThh:mm` bzw. „Tag 12, 06:00“). */
export function momentText(m: Moment): string {
  if (m.basis === 'ms') return stampText(m.value);
  const unit = m.unit ?? 'days';
  return formatCount(unit, m.value / UNIT_MINUTES[unit], DEFAULT_TIME_DISPLAY);
}

const COUNT_UNIT: Record<string, TimeUnit> = { minute: 'minutes', stunde: 'hours', tag: 'days', jahr: 'years' };
const REL_MINUTES: Record<string, number> = { min: 1, h: 60, d: 1440, w: 10080, mo: 43200, y: 525960 };
/** Minuten je Einheit eines Abstands; eine Woche hat so viele Tage wie die Woche des Kalenders */
const relMinutes = (unit: string): number => (unit === 'w' ? weekLength() * 1440 : REL_MINUTES[unit]!);

/**
 * Zeitpunkt einer Marke: absolut für sich, relativ ausgehend von `base` (die vorige Marke bzw. die Zeit der Ankerszene). null, wenn er
 * sich nicht bestimmen lässt (relativ ohne `base`, Tag im Jahr ohne Datum).
 */
export function markMoment(value: string, base: Moment | null): Moment | null {
  let m = ABS.exec(value);
  if (m) {
    const t = utc(Number(m[1]), Number(m[2] ?? 1), Number(m[3] ?? 1), Number(m[4] ?? 0), Number(m[5] ?? 0));
    return t === null ? null : { basis: 'ms', value: t, unit: null };
  }
  if ((m = YEARLESS.exec(value))) {
    if (base?.basis !== 'ms') return null;
    const year = msToParts(base.value).y;
    const at = (y: number): number | null => utc(y, Number(m![1]), Number(m![2]), Number(m![3] ?? 0), Number(m![4] ?? 0));
    // „am 3. März“ nach dem 5. März: im Jahr darauf (ein Schalttag, den es dieses Jahr nicht gibt, ebenso)
    const t = at(year);
    const next = t === null || t < base.value - DAY_LENGTH_MS ? at(year + 1) : t;
    return next === null ? null : { basis: 'ms', value: next, unit: null };
  }
  if ((m = COUNT.exec(value))) {
    const unit = COUNT_UNIT[m[1]!]!;
    const clock = m[3] !== undefined ? Number(m[3]) * 60 + Number(m[4]) : 0;
    return { basis: 'count', value: Number(m[2]) * UNIT_MINUTES[unit] + clock, unit };
  }
  if ((m = REL.exec(value))) {
    if (!base) return null;
    const n = (m[1] === '-' ? -1 : 1) * Number(m[2]);
    const unit = m[3]!;
    const clock = m[4] !== undefined ? Number(m[4]) * 60 + Number(m[5]) : null;
    if (base.basis === 'ms') {
      let v = unit === 'mo' ? addMonths(base.value, n) : unit === 'y' ? addYears(base.value, n) : base.value + n * relMinutes(unit) * 60000;
      // Tage beginnen in beiden Kalendern bei einem Vielfachen eines Tages (gregorianisch: Mitternacht UTC)
      if (clock !== null) v = Math.floor(v / DAY_LENGTH_MS) * DAY_LENGTH_MS + clock * 60000;
      return { basis: 'ms', value: v, unit: null };
    }
    let v = base.value + n * relMinutes(unit);
    if (clock !== null) v = Math.floor(v / 1440) * 1440 + clock;
    return { basis: 'count', value: v, unit: base.unit };
  }
  return null;
}

/** Was eine Marke zur Zeit einer Szene beiträgt */
export type MarkInfo = Pick<TimeMarkRef, 'value' | 'precision' | 'approx'> & { role?: MarkRole | null };

/** Eine Marke im Zusammenhang ihres Textes: ihr Zeitpunkt und wovon sie ausging */
export interface ResolvedMark {
  /** Zeitpunkt; null: eine Dauer bzw. nicht bestimmbar */
  moment: Moment | null;
  /** relativ: wovon sie ausging (die Marke davor bzw. der Anker); absolut null */
  from: Moment | null;
  /** sie ging vom Anker aus (keine bestimmbare Marke davor) */
  usedBase: boolean;
}

export interface ResolvedMarks {
  marks: ResolvedMark[];
  /** Stelle der Marke, die den Beginn bestimmt, des Endes bzw. der Dauer (-1: keine) */
  start: number;
  end: number;
  duration: number;
}

/**
 * Die Zeitmarken eines Textes der Reihe nach auflösen (PLAN.md §16, §17): absolute Marken stehen für sich, relative gehen von der
 * vorigen aus, die erste relative von `base` (der Zeit der Ankerszene). Beginn = die erste als Beginn gekennzeichnete Marke, sonst die
 * erste ohne Rolle; Ende = die letzte als Ende gekennzeichnete; Dauer = die letzte Dauer.
 */
export function resolveMarks(marks: readonly MarkInfo[], base: Moment | null): ResolvedMarks {
  let cursor: Moment | null = null;
  let auto = -1;
  let explicit = -1;
  let end = -1;
  let duration = -1;
  const out = marks.map((mark, i): ResolvedMark => {
    const role = markRole({ value: mark.value, role: mark.role ?? null });
    if (role === 'duration') {
      duration = i;
      return { moment: null, from: null, usedBase: false };
    }
    const from = isRelativeMark(mark.value) ? (cursor ?? base) : null;
    const m = markMoment(mark.value, from);
    const usedBase = from !== null && cursor === null;
    if (!m) return { moment: null, from, usedBase };
    cursor = m;
    if (role === 'end') end = i;
    else if (role === 'start') explicit = explicit < 0 ? i : explicit;
    else auto = auto < 0 ? i : auto;
    return { moment: m, from, usedBase };
  });
  return { marks: out, start: explicit >= 0 ? explicit : auto, end, duration };
}

/**
 * Zeit einer Szene aus den Zeitmarken ihres Textes, in Textreihenfolge (`resolveMarks`): Beginn mit seiner Genauigkeit und „ungefähr“;
 * Ende = die letzte als Ende gekennzeichnete Marke (nach dem Beginn), sonst Beginn + die Dauer einer Dauer-Marke; ohne beides ist die
 * Szene ein Zeitpunkt. `usesBase`: der Beginn hing an `base`.
 */
export function timeFromMarks(marks: readonly MarkInfo[], base: Moment | null): { time: SceneTime | null; usesBase: boolean } {
  const r = resolveMarks(marks, base);
  if (r.start < 0) return { time: null, usesBase: false };
  const startMark = marks[r.start]!;
  const start = r.marks[r.start]!.moment!;
  const time: SceneTime = { start: momentText(start), end: null, source: 'text' };
  const end = r.end >= 0 ? r.marks[r.end]!.moment : null;
  if (end && end.basis === start.basis && end.value > start.value) time.end = momentText(end);
  else if (r.duration >= 0) {
    const duration = marks[r.duration]!.value;
    time.duration = duration;
    const e = markMoment(`+${duration}`, start);
    time.end = e ? momentText(e) : null;
  }
  const precision = startMark.precision ?? impliedPrecision(startMark.value);
  if (precision !== 'minute') time.precision = precision;
  if (startMark.approx) time.approx = true;
  return { time, usesBase: r.marks[r.start]!.usedBase };
}
